import { ErrorHandler, Injectable } from '@angular/core';
import { OrderState } from 'src/app/module/order/state/order.state';
import { ScenarioState } from 'src/app/module/scenario/state/scenario.state';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../services/logging.service';
import { SnackBarService } from '../services/snack-bar.service';
import { CompanyState } from '../state/company.state';
import { UserState } from '../state/user.state';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    private companyState: CompanyState,
    private loggingService: LoggerService,
    private orderState: OrderState,
    private scenarioState: ScenarioState,
    private snackBarService: SnackBarService,
    private userState: UserState
  ) {
    // catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(event.reason ?? 'Unhandled promise rejection');
    });
  }

  /**
   * Handles errors globally across the application.
   * Implements rate limiting, deduplication, and error loop detection to prevent overwhelming the logging API.
   */
  handleError(error: any): void {
    let message = 'Unknown error';
    let stack = '';

    if (error instanceof Error) {
      message = error.message;
      stack = error.stack ?? '';
    } else if (typeof error === 'string') {
      message = error;
    } else {
      try {
        message = JSON.stringify(error);
      } catch {
        message = 'Unserializable error';
      }
    }

    // Check if error should be logged based on rate limiting, deduplication, and error loop detection
    const checkResult = this.loggingService.shouldLogError(message, stack);

    if (checkResult.isErrorLoop) {
      console.error(error);

      // Error loop detected - show user notification and suppress error to break the loop
      console.warn(
        '⚠️ ERROR LOOP DETECTED: Same error occurred 3+ times consecutively. Logging has been suppressed to prevent infinite loop.'
      );

      // Show snackbar notification to user (only once)
      // this.snackBarService.open('error', 'Something went wrong. Please refresh the application to continue.');

      // Suppress error re-throw to break the infinite loop
      return;
    }

    if (checkResult.isRateLimited) {
      // Rate limit exceeded - log to console but don't send to API
      console.warn('⚠️ Error rate limit exceeded. Error not logged to API:', checkResult.reason);

      // Still re-throw the error for normal error handling
      throw error;
    }

    if (!checkResult.shouldLog) {
      // Error is duplicate - silently skip logging
      console.debug('ℹ️ Duplicate error suppressed:', checkResult.reason);

      // Still re-throw the error for normal error handling
      throw error;
    }

    // Normal case - log to API (only if not running locally)
    if (!environment.isRunningLocally) {
      this.loggingService.logError(message, stack, {
        url: window.location.href,
        time: new Date().toISOString(),
        orderId: this.orderState.orderDetail.orderId,
        scenarioId: this.scenarioState.activeScenario?.id,
        companyId: this.companyState.companySetting.id,
        userId: this.userState.userSetting.id,
      });
    }

    throw error;
  }
}
